import React, { useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { UserPlusIcon } from '@heroicons/react/24/outline'
import { RadialBackgroundContainer } from '../../../../interaxDesignSystemComponents'
import { DefaultModal } from '../../../../shared/components/DefaultModal'
import { INTERAX_COLORS } from '../../../../constants/colors'
import useAppState from '../../../../hooks/useAppState'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { InviteUsersPreviewList } from './InviteUsersPreviewList'
import { InviteUsersSuccessPanel } from './InviteUsersSuccessPanel'
import { InviteUsersFormPanel } from './InviteUsersFormPanel'
import { InviteUsersActionsBar } from './InviteUsersActionsBar'
import { InviteUsersConfirmPanel } from './InviteUsersConfirmPanel'
import { useInviteUsersWorkflow } from './useInviteUsersWorkflow'

interface InviteUsersModalProps {
  isOpen: boolean
  onClose: () => void
}

export const InviteUsersModal: React.FC<InviteUsersModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { state } = useAppState()

  const {
    control,
    errors,
    isSubmitting,
    editingIndex,
    previewUsers,
    totalUsers,
    isConfirming,
    serverError,
    actionDisabled,
    hasPendingUser,
    isClearDisabled,
    result,
    handleClose,
    handleSaveUser,
    handleClearList,
    handlePrepareConfirmation,
    handleCancelConfirmation,
    handleConfirmSend,
    handleSelectUser,
    handleRemoveFromList,
    handleRestart
  } = useInviteUsersWorkflow({ isOpen, onClose, t, state })

  const previewCopy = useMemo(
    () => ({
      title: t('facilitator.profile.inviteUsers.previewTitle'),
      nameFallback: t('facilitator.profile.inviteUsers.previewMissingName'),
      emailFallback: t('facilitator.profile.inviteUsers.previewMissingEmail')
    }),
    [t]
  )

  const formInputLabels = useMemo(
    () => ({
      name: t('facilitator.profile.inviteUsers.nameLabel'),
      email: t('facilitator.profile.inviteUsers.emailLabel'),
      namePlaceholder: t('facilitator.profile.inviteUsers.namePlaceholder'),
      emailPlaceholder: t('facilitator.profile.inviteUsers.emailPlaceholder')
    }),
    [t]
  )

  const formButtonLabels = useMemo(
    () => ({
      add: t('facilitator.profile.inviteUsers.addUser'),
      update: t('facilitator.profile.inviteUsers.updateUser')
    }),
    [t]
  )

  const actionLabels = useMemo(
    () => ({
      clear: t('facilitator.profile.inviteUsers.clearButton'),
      invite: t('facilitator.profile.inviteUsers.inviteButton')
    }),
    [t]
  )

  const confirmCopy = useMemo(
    () => ({
      heading: t('facilitator.profile.inviteUsers.confirmHeading', { count: totalUsers }),
      body: t('facilitator.profile.inviteUsers.confirmBody'),
      helper: t('facilitator.profile.inviteUsers.confirmHelper'),
      confirm: t('facilitator.profile.inviteUsers.confirmYes'),
      cancel: t('facilitator.profile.inviteUsers.confirmNo'),
      loading: t('common.loading')
    }),
    [t, totalUsers]
  )

  if (result) {
    return (
      <DefaultModal isOpen={isOpen} onClose={handleClose} backgroundColor={INTERAX_COLORS.heritageBlue40}>
        <RadialBackgroundContainer useFixedHeight={false}>
          <InviteUsersSuccessPanel invitedUsers={result.invitedUsers} onRestart={handleRestart} />
        </RadialBackgroundContainer>
      </DefaultModal>
    )
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={handleClose}
      backgroundColor={INTERAX_COLORS.heritageBlue40}
      maxWidth="700px"
    >
      <RadialBackgroundContainer useFixedHeight={false}>
        <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '100%', md: '90vw' }, mx: 'auto' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'column', md: 'row' },
              gap: { xs: '16px', sm: '16px', md: '24px' },
              alignItems: 'stretch',
              minHeight: 0
            }}
          >
            <Box
              sx={{
                flex: '1 1 55%',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                minWidth: 0
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: INTERAX_COLORS.white }}>
                  <UserPlusIcon className="w-6 h-6" />
                  <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
                    {t('facilitator.profile.inviteUsers.title')}
                  </Typography>
                </Box>
              </Box>

              {!isConfirming ? (
                <>
                  <InviteUsersFormPanel
                    control={control}
                    errors={errors}
                    serverError={serverError}
                    editingIndex={editingIndex}
                    isSubmitting={isSubmitting}
                    actionDisabled={actionDisabled}
                    onSave={handleSaveUser}
                    inputLabels={formInputLabels}
                    buttonLabels={formButtonLabels}
                  />
                  <InviteUsersActionsBar
                    onClear={handleClearList}
                    onInvite={handlePrepareConfirmation}
                    clearDisabled={isClearDisabled}
                    inviteDisabled={actionDisabled || !hasPendingUser}
                    clearLabel={actionLabels.clear}
                    inviteLabel={actionLabels.invite}
                  />
                </>
              ) : (
                <InviteUsersConfirmPanel
                  heading={confirmCopy.heading}
                  body={confirmCopy.body}
                  helper={confirmCopy.helper}
                  confirmLabel={confirmCopy.confirm}
                  cancelLabel={confirmCopy.cancel}
                  loadingLabel={confirmCopy.loading}
                  isLoading={actionDisabled}
                  onConfirm={handleConfirmSend}
                  onCancel={handleCancelConfirmation}
                />
              )}
            </Box>

            <InviteUsersPreviewList
              users={previewUsers}
              title={previewCopy.title}
              nameFallback={previewCopy.nameFallback}
              emailFallback={previewCopy.emailFallback}
              removeLabel={t('facilitator.profile.inviteUsers.removeUser')}
              activeIndex={editingIndex}
              onSelect={handleSelectUser}
              onRemove={handleRemoveFromList}
            />
          </Box>
        </Box>
      </RadialBackgroundContainer>
    </DefaultModal>
  )
}
